import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { restaurantsApi, exploitationApi } from '../lib/api';
import { cn } from '../lib/utils';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { Skeleton } from '../components/ui/skeleton';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
    Building2,
    Layers,
    PlaneLanding,
    ChevronRight,
    ChevronDown,
    FileSpreadsheet,
    Download,
    FileText,
    Table2
} from 'lucide-react';

// Logos des restaurants
const restaurantLogos = {
    "Ma biche sur le toit": "/logos/ma-biche-sur-le-toit.png",
    "Compagnie Française": "/logos/compagnie-francaise.png",
    "Compagnie Busca": "/logos/compagnie-busca.png",
    "Jacques Balma": "/logos/jacques-balma.png",
    "Jacques Blagnac": "/logos/jacques-blagnac.png",
    "Jacques Fenouillet": "/logos/jacques-fenouillet.png",
    "Café Chouchou": "/logos/cafe-chouchou.png",
    "Coup de bambou": "/logos/coup-de-bambou.png",
    "Sous les pins": "/logos/sous-les-pins.png",
};

// Ordre personnalisé des restaurants (Ma Biche en premier)
const RESTAURANT_ORDER= [
    "Ma biche sur le toit",
    "Compagnie Française",
    "Compagnie Busca",
    "Jacques Balma",
    "Jacques Blagnac",
    "Jacques Fenouillet",
    "Café Chouchou",
    "Coup de bambou"
];

// Restaurant supplémentaire visible uniquement sur cette page (pas en DB)
const EXTRA_EXPLOITATION_RESTAURANTS = [
    { id: 'resto-fenouillet', name: 'Jacques Fenouillet' }
];

// Restaurants exclus (pas de données SharePoint) - liste vide, tous les restaurants sont affichés
const EXCLUDED_RESTAURANTS = [];

// Mois en français
const MONTHS_FR = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' },
];

// Formater un montant
const formatMoney = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

// Formater un pourcentage
const formatPct = (value, ca) => {
    if (!ca || ca === 0 || value === null || value === undefined) return '-';
    const pct = (value / ca) * 100;
    return `${pct.toFixed(1)}%`;
};

// Structure du SIG
const sigStructure = [
    { id: 'ca_net', label: 'Chiffre d\'affaires net', type: 'produit', level: 0, highlight: true, expandable: true },
    { id: 'achats_consommes', label: 'Achats consommés (matières premières)', type: 'charge', level: 1, expandable: true },
    { id: 'marge_brute', label: 'MARGE BRUTE', type: 'solde', level: 0, highlight: true, isResult: true },
    { id: 'prestations_annexes', label: 'Prestations et produits annexes', type: 'produit', level: 1, expandable: true },
    { id: 'charges_externes', label: 'Autres achats et charges externes', type: 'charge', level: 1, expandable: true },
    { id: 'autres_produits', label: 'Subventions et autres produits d\'exploitation', type: 'produit', level: 1, expandable: true },
    { id: 'valeur_ajoutee', label: 'VALEUR AJOUTÉE', type: 'solde', level: 0, highlight: true, isResult: true },
    { id: 'impots_taxes', label: 'Impôts, taxes et versements assimilés', type: 'charge', level: 1, expandable: true },
    { id: 'charges_personnel', label: 'Charges de personnel', type: 'charge', level: 1, expandable: true },
    { id: 'remuneration_dirigeants', label: 'Rémunération des dirigeants', type: 'charge', level: 1, expandable: true },
    { id: 'ebe', label: 'EXCÉDENT BRUT D\'EXPLOITATION (EBE)', type: 'solde', level: 0, highlight: true, isResult: true, final: true },
    { id: 'reprises_provisions', label: 'Reprises sur provisions', type: 'produit', level: 1, expandable: true },
    { id: 'dotations_amort_prov', label: 'Dotations aux amortissements et provisions', type: 'charge', level: 1 },
    { id: 'autres_charges', label: 'Autres charges de gestion courante', type: 'charge', level: 1, expandable: true },
    { id: 'resultat_exploitation', label: 'RÉSULTAT D\'EXPLOITATION', type: 'solde', level: 0, highlight: true, isResult: true },
    { id: 'resultat_financier', label: 'Résultat financier', type: 'solde', level: 1 },
    { id: 'rcai', label: 'RÉSULTAT COURANT AVANT IMPÔTS', type: 'solde', level: 0, highlight: true, isResult: true },
    { id: 'resultat_exceptionnel', label: 'Résultat exceptionnel', type: 'solde', level: 1, expandable: true },
    { id: 'impot_societes', label: 'Impôt sur les bénéfices', type: 'charge', level: 1 },
    { id: 'resultat_net', label: 'RÉSULTAT NET', type: 'solde', level: 0, highlight: true, isResult: true, final: true },
    { id: 'quote_part_subventions_caf', label: 'Quote-part subventions virées (747 & 777)', type: 'charge', level: 1 },
    { id: 'caf', label: 'CAPACITÉ D\'AUTOFINANCEMENT (CAF)', type: 'solde', level: 0, highlight: true, isResult: true, final: true },
];

// Composant pour une ligne de détail (compte individuel)
const DetailRow = ({ 
    compte, 
    libelle, 
    monthMontant, 
    ytdMontant, 
    monthCa, 
    ytdCa,
    monthMontantN1 = null,
    ytdMontantN1 = null,
    monthCaN1 = null,
    ytdCaN1 = null,
    showComparison = true,
    displayMode = 'month',
    isCharge = false  // Pour la logique de couleur des écarts
}) => {
    // Sélectionner les valeurs selon le mode
    const currentMontant = displayMode === 'ytd' ? ytdMontant : monthMontant;
    const currentMontantN1 = displayMode === 'ytd' ? ytdMontantN1 : monthMontantN1;
    const currentCa = displayMode === 'ytd' ? ytdCa : monthCa;
    const currentCaN1 = displayMode === 'ytd' ? ytdCaN1 : monthCaN1;
    
    const variation = calculateVariation(currentMontant, currentMontantN1);
    const ecart = calculateEcartValue(currentMontant, currentMontantN1);
    
    // Couleur de l'écart selon le type (charge vs produit)
    const getDetailEcartColor = () => {
        if (ecart === null) return 'text-muted-foreground';
        // Pour les charges, une baisse (écart négatif) est positive → vert
        if (isCharge) {
            return ecart <= 0 ? 'text-green-600' : 'text-red-600';
        }
        // Pour les produits, une hausse (écart positif) est positive → vert
        return ecart >= 0 ? 'text-green-600' : 'text-red-600';
    };
    
    const gridClass = showComparison 
        ? "grid grid-cols-[1fr,110px,65px,110px,65px,85px] gap-0 border-b border-border/20 bg-slate-50/50 hover:bg-slate-100/50"
        : "grid grid-cols-[1fr,120px,80px] gap-0 border-b border-border/20 bg-slate-50/50 hover:bg-slate-100/50";
    
    return (
        <div className={gridClass}>
            <div className="py-1.5 px-3 pl-10 text-xs text-muted-foreground flex items-center gap-2">
                <span className="font-mono text-[10px] bg-slate-200/80 px-1.5 py-0.5 rounded">{compte}</span>
                <span className="truncate">{libelle}</span>
            </div>
            
            {/* Valeur 2026 */}
            <div className={cn(
                "py-1.5 px-2 text-right text-xs tabular-nums",
                displayMode === 'ytd' ? "bg-emerald-50/30" : "bg-blue-50/30"
            )}>
                {formatMoney(currentMontant)}
            </div>
            <div className={cn(
                "py-1.5 px-2 text-right text-xs tabular-nums text-muted-foreground",
                displayMode === 'ytd' ? "bg-emerald-50/20" : "bg-blue-50/20"
            )}>
                {formatPct(currentMontant, currentCa)}
            </div>
            
            {showComparison && (
                <>
                    {/* Valeur N-1 */}
                    <div className="py-1.5 px-2 text-right text-xs tabular-nums text-muted-foreground bg-slate-100/50">
                        {formatMoney(currentMontantN1 ?? 0)}
                    </div>
                    <div className="py-1.5 px-2 text-right text-xs tabular-nums text-muted-foreground bg-slate-100/30">
                        {currentMontantN1 !== null && currentMontantN1 !== undefined ? formatPct(currentMontantN1, currentCaN1) : '0.0%'}
                    </div>
                    {/* Écart */}
                    <div 
                        className={cn(
                            "py-1.5 px-2 text-right text-xs tabular-nums cursor-help",
                            getDetailEcartColor()
                        )}
                        title={variation !== null ? `Variation: ${formatVariation(variation)}` : undefined}
                    >
                        {formatEcartValue(ecart)}
                    </div>
                </>
            )}
        </div>
    );
};

// Fonction pour calculer l'écart en %
const calculateVariation = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / Math.abs(previous)) * 100;
};

// Fonction pour calculer l'écart en valeur
// Si N-1 est null/undefined, on considère 0 pour calculer l'écart
const calculateEcartValue = (current, previous) => {
    if (current === null || current === undefined) return null;
    const prev = (previous === null || previous === undefined) ? 0 : previous;
    return current - prev;
};

// Formater l'écart en %
const formatVariation = (variation) => {
    if (variation === null || variation === undefined) return '-';
    const sign = variation >= 0 ? '+' : '';
    return `${sign}${variation.toFixed(1)}%`;
};

// Formater l'écart en valeur (K€ avec 1 décimale)
const formatEcartValue = (ecart) => {
    if (ecart === null || ecart === undefined) return '-';
    const sign = ecart >= 0 ? '+' : '';
    const valueInK = ecart / 1000;
    return `${sign}${valueInK.toFixed(1)} K€`;
};

// Composant pour une ligne du SIG avec comparaison N-1
// Noms courts pour les infobulles
const SHORT_NAMES = {
    "Ma biche sur le toit": "Ma Biche",
    "Compagnie Française": "Cie Française",
    "Compagnie Busca": "Cie Busca",
    "Jacques Balma": "J. Balma",
    "Jacques Blagnac": "J. Blagnac",
    "Jacques Fenouillet": "J. Fenouillet",
    "Café Chouchou": "Chouchou",
    "Coup de bambou": "Bambou",
    "Sous les pins": "Sous les pins"
};

// Infobox flottante affichée au survol d'une ligne du SIG
const SigHoverBox = ({ pos, item, periodLabel, compareLabel, current, compare, currentCa, compareCa, isCharge, rows, pctMode = false, currentLabel = 'Réalisé' }) => {
    if (!pos) return null;
    const ecart = (current ?? 0) - (compare ?? 0);
    const pct = compare ? (ecart / Math.abs(compare)) * 100 : null;
    // Mode % CA (achats consommés) : l'écart est exprimé en points de CA
    const toPct = (v, ca) => (ca ? (v / ca) * 100 : null);
    const pctCur = toPct(current ?? 0, currentCa);
    const pctCmp = compare === null || compare === undefined ? null : toPct(compare, compareCa);
    const ecartPts = pctCur !== null && pctCmp !== null ? pctCur - pctCmp : null;
    const fmtPts = (v) => v === null ? '-' : `${v >= 0 ? '+' : ''}${v.toFixed(1)} pt`;
    const good = pctMode ? (ecartPts ?? 0) <= 0 : (isCharge ? ecart <= 0 : ecart >= 0);
    const boxWidth = 460;
    const left = pos.x + boxWidth + 24 > window.innerWidth ? pos.x - boxWidth - 12 : pos.x + 16;
    const top = Math.min(pos.y + 16, window.innerHeight - 60 - Math.min(rows.length, 10) * 22 - 120);
    const fmtK = (v) => `${v >= 0 ? '+' : '-'}${(Math.abs(v) / 1000).toFixed(1)}K€`;
    
    return (
        <div
            className="fixed z-[100] pointer-events-none rounded-lg bg-slate-900/95 text-white shadow-2xl border border-slate-700 text-xs"
            style={{ left, top: Math.max(8, top), width: boxWidth }}
            data-testid="sig-hover-box"
        >
            <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between gap-2">
                <span className="font-semibold truncate">{item.label}</span>
                <span className="text-slate-400 whitespace-nowrap">{periodLabel}</span>
            </div>
            <div className="px-3 py-2 grid grid-cols-3 gap-2">
                <div>
                    <div className="text-slate-400">{currentLabel}</div>
                    <div className="tabular-nums font-medium">{formatMoney(current ?? 0)}</div>
                    <div className="text-slate-500">{formatPct(current, currentCa)}</div>
                </div>
                <div>
                    <div className="text-slate-400">{compareLabel}</div>
                    <div className="tabular-nums font-medium">{compare === null || compare === undefined ? '-' : formatMoney(compare)}</div>
                    <div className="text-slate-500">{compare === null || compare === undefined ? '' : formatPct(compare, compareCa)}</div>
                </div>
                <div>
                    <div className="text-slate-400">{pctMode ? `Écart % CA vs ${compareLabel}` : `Écart vs ${compareLabel}`}</div>
                    <div className={cn("tabular-nums font-semibold", good ? "text-emerald-400" : "text-red-400")}>
                        {pctMode ? fmtPts(ecartPts) : formatEcartValue(ecart)}
                    </div>
                    <div className={cn("tabular-nums", good ? "text-emerald-500/80" : "text-red-400/80")}>
                        {pctMode ? formatEcartValue(ecart) : (pct !== null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : '')}
                    </div>
                </div>
            </div>
            {rows.length > 0 && (
                <div className="border-t border-slate-700 px-3 py-2">
                    <div className="grid grid-cols-[1fr,84px,84px,66px] gap-1 text-slate-400 mb-1">
                        <span>{rows[0].isRestaurant ? 'Restaurant' : 'Compte'}</span>
                        <span className="text-right">{pctMode ? `${currentLabel} % CA` : currentLabel}</span>
                        <span className="text-right">{pctMode ? `${compareLabel} % CA` : compareLabel}</span>
                        <span className="text-right">{pctMode ? 'Écart pt' : 'Écart'}</span>
                    </div>
                    {rows.slice(0, 10).map((r) => {
                        const noCmp = r.compare === null;
                        const e = r.current - (r.compare ?? 0);
                        // En consolidé chaque restaurant a son propre CA ; sinon CA du restaurant
                        const rCur = toPct(r.current, r.isRestaurant ? r.currentCa : currentCa);
                        const rCmp = noCmp ? null : toPct(r.compare, r.isRestaurant ? r.compareCa : compareCa);
                        const ePts = rCur !== null && rCmp !== null ? rCur - rCmp : null;
                        const g = pctMode ? (ePts ?? 0) <= 0 : (isCharge ? e <= 0 : e >= 0);
                        return (
                            <div key={r.key} className="grid grid-cols-[1fr,84px,84px,66px] gap-1 leading-[22px]">
                                <span className="truncate" title={r.label}>{r.label}</span>
                                <span className="text-right tabular-nums">{pctMode ? (rCur === null ? '-' : `${rCur.toFixed(1)}%`) : formatMoney(r.current)}</span>
                                <span className="text-right tabular-nums text-slate-400">{noCmp ? '-' : pctMode ? (rCmp === null ? '-' : `${rCmp.toFixed(1)}%`) : formatMoney(r.compare)}</span>
                                <span className={cn("text-right tabular-nums font-medium", noCmp ? "text-slate-500" : g ? "text-emerald-400" : "text-red-400")}>{noCmp ? '-' : pctMode ? fmtPts(ePts) : fmtK(e)}</span>
                            </div>
                        );
                    })}
                    {rows.length > 10 && <div className="text-slate-500 mt-1">… {rows.length - 10} autres lignes (cliquer pour déplier)</div>}
                </div>
            )}
        </div>
    );
};

const SigRow = ({ 
    item, 
    monthValue, 
    ytdValue, 
    monthCa,
    ytdCa,
    monthDetails = [],
    ytdDetails = [],
    // Données N-1
    monthValueN1 = null,
    ytdValueN1 = null,
    monthCaN1 = null,
    ytdCaN1 = null,
    monthDetailsN1 = [],
    ytdDetailsN1 = [],
    showComparison = true,
    strictEcart = false,
    displayMode = 'month', // 'month' ou 'ytd'
    // Données par restaurant pour tooltip consolidation
    byRestaurant = null,
    isConsolidated = false,
    compareMode = 'n1', // 'n1' ou 'budget' : pilote l'infobox de survol
    periodLabel = ''
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [hoverPos, setHoverPos] = useState(null);
    
    // Vérifier les détails des DEUX années (2026 ET 2025) pour permettre l'expansion
    // même si un compte existait uniquement en 2025
    const hasDetails = item.expandable && (
        (monthDetails && monthDetails.length > 0) || 
        (ytdDetails && ytdDetails.length > 0) ||
        (monthDetailsN1 && monthDetailsN1.length > 0) ||
        (ytdDetailsN1 && ytdDetailsN1.length > 0)
    );
    
    // Sélectionner les valeurs selon le mode d'affichage
    const currentValue = displayMode === 'ytd' ? ytdValue : monthValue;
    const currentValueN1 = displayMode === 'ytd' ? ytdValueN1 : monthValueN1;
    const currentCa = displayMode === 'ytd' ? ytdCa : monthCa;
    const currentCaN1 = displayMode === 'ytd' ? ytdCaN1 : monthCaN1;
    const currentDetails = displayMode === 'ytd' ? ytdDetails : monthDetails;
    const currentDetailsN1 = displayMode === 'ytd' ? ytdDetailsN1 : monthDetailsN1;
    
    // Calculer les écarts
    const noComparisonValue = currentValueN1 === null || currentValueN1 === undefined;
    const ecart = (strictEcart && noComparisonValue) ? null : calculateEcartValue(currentValue, currentValueN1);
    
    const compareLabel = compareMode === 'budget' ? 'Budget' : 'N-1';
    
    // Lignes de l'infobox : par restaurant (consolidé) ou par compte (détails), triées par écart décroissant
    const getHoverRows = () => {
        if (!showComparison) return [];
        const rows = [];
        if (isConsolidated && byRestaurant) {
            const dataKey = displayMode === 'ytd' ? 'ytd_data' : 'month_data';
            const cmpKey = displayMode === 'ytd' ? `ytd_data_${compareMode}` : `month_data_${compareMode}`;
            Object.entries(byRestaurant).forEach(([name, data]) => {
                const cur = data[dataKey]?.[item.id];
                const cmp = data[cmpKey]?.[item.id];
                if ((cur ?? null) !== null || (cmp ?? null) !== null) {
                    rows.push({
                        key: name, label: SHORT_NAMES[name] || name, current: cur ?? 0,
                        compare: (cmp ?? null) === null ? null : cmp, isRestaurant: true,
                        currentCa: data[dataKey]?.ca_net, compareCa: data[cmpKey]?.ca_net
                    });
                }
            });
        } else {
            const map = {};
            (currentDetails || []).forEach(d => { map[d.compte] = { key: d.compte, label: `${d.compte} ${d.libelle || ''}`.trim(), current: d.montant || 0, compare: 0 }; });
            (currentDetailsN1 || []).forEach(d => {
                if (!map[d.compte]) map[d.compte] = { key: d.compte, label: `${d.compte} ${d.libelle || ''}`.trim(), current: 0, compare: 0 };
                map[d.compte].compare = d.montant || 0;
            });
            rows.push(...Object.values(map));
        }
        return rows.sort((a, b) => Math.abs(b.current - (b.compare ?? 0)) - Math.abs(a.current - (a.compare ?? 0)));
    };
    const hoverRows = hoverPos ? getHoverRows() : [];
    const showHoverBox = showComparison && isConsolidated;
    
    // Couleur du résultat
    const getValueColor = (value, isResult) => {
        if (!isResult) return '';
        if (value > 0) return 'text-green-600';
        if (value < 0) return 'text-red-600';
        return '';
    };
    
    // Couleur de l'écart (basée sur la valeur)
    const getEcartColor = (ecartVal, isCharge) => {
        if (ecartVal === null) return 'text-muted-foreground';
        // Pour les charges, une baisse est positive
        if (isCharge) {
            return ecartVal <= 0 ? 'text-green-600' : 'text-red-600';
        }
        // Pour les produits/soldes, une hausse est positive
        return ecartVal >= 0 ? 'text-green-600' : 'text-red-600';
    };
    
    const isCharge = item.type === 'charge';
    
    // Grille simplifiée : Label | 2026 | % | 2025 | % | Écart
    const gridClass = showComparison 
        ? "grid grid-cols-[1fr,110px,65px,110px,65px,85px] gap-0 border-b transition-colors"
        : "grid grid-cols-[1fr,120px,80px] gap-0 border-b transition-colors";
    
    return (
        <>
            <div 
                className={cn(
                    gridClass,
                    item.highlight && "bg-slate-100 font-semibold",
                    item.final && "bg-amber-50 font-bold border-t-2 border-amber-400",
                    !item.highlight && !item.final && "hover:bg-slate-50",
                    hasDetails && "cursor-pointer"
                )}
                onClick={() => hasDetails && setIsExpanded(!isExpanded)}
                onMouseEnter={(e) => showHoverBox && setHoverPos({ x: e.clientX, y: e.clientY })}
                onMouseMove={(e) => showHoverBox && setHoverPos({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setHoverPos(null)}
                data-testid={`sig-row-${item.id}`}
            >
                {/* Label */}
                <div className={cn(
                    "py-2.5 px-3 flex items-center gap-2 text-sm",
                    item.level === 1 && "pl-6",
                    item.level === 2 && "pl-10 text-xs italic"
                )}>
                    {hasDetails && (
                        isExpanded 
                            ? <ChevronDown className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                            : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    )}
                    <span className="truncate">{item.label}</span>
                </div>
                
                {/* === Valeur 2026 === */}
                <div className={cn(
                    "py-2.5 px-2 text-right tabular-nums text-sm",
                    displayMode === 'ytd' ? "bg-emerald-50/50" : "bg-blue-50/50",
                    item.final && "bg-amber-50",
                    getValueColor(currentValue, item.isResult)
                )}>
                    {formatMoney(currentValue)}
                </div>
                <div className={cn(
                    "py-2.5 px-2 text-right tabular-nums text-muted-foreground text-xs",
                    displayMode === 'ytd' ? "bg-emerald-50/30" : "bg-blue-50/30",
                    item.final && "bg-amber-50/50"
                )}>
                    {formatPct(currentValue, currentCa)}
                </div>
                
                {showComparison && (
                    <>
                        {/* === Valeur N-1 === */}
                        <div className={cn(
                            "py-2.5 px-2 text-right tabular-nums text-sm bg-slate-50/50 text-muted-foreground",
                            item.final && "bg-amber-50/70"
                        )}>
                            {(strictEcart && noComparisonValue) ? '-' : formatMoney(currentValueN1 ?? 0)}
                        </div>
                        <div className={cn(
                            "py-2.5 px-2 text-right tabular-nums text-xs bg-slate-50/30 text-muted-foreground",
                            item.final && "bg-amber-50/50"
                        )}>
                            {(strictEcart && noComparisonValue) ? '-' : (currentValueN1 !== null ? formatPct(currentValueN1, currentCaN1) : '0.0%')}
                        </div>
                        
                        {/* === ÉCART === */}
                        <div 
                            className={cn(
                                "py-2.5 px-2 text-right tabular-nums text-xs font-medium cursor-help",
                                getEcartColor(ecart, isCharge),
                                item.final && "bg-amber-50/30"
                            )}
                        >
                            {formatEcartValue(ecart)}
                        </div>
                    </>
                )}
            </div>
            
            {showHoverBox && hoverPos && (
                <SigHoverBox
                    pos={hoverPos}
                    item={item}
                    periodLabel={periodLabel}
                    compareLabel={compareLabel}
                    current={currentValue}
                    compare={(strictEcart && noComparisonValue) ? null : (currentValueN1 ?? 0)}
                    currentCa={currentCa}
                    compareCa={currentCaN1}
                    isCharge={isCharge}
                    rows={(item.id === 'achats_consommes' && compareMode === 'budget') ? [] : hoverRows}
                    pctMode={item.id === 'achats_consommes'}
                    currentLabel={periodLabel.startsWith('Atterrissage') ? 'Atterrissage' : 'Réalisé'}
                />
            )}
            
            {/* Détails dépliables */}
            {hasDetails && isExpanded && (
                <div className="border-b border-border/30">
                    {(() => {
                        // Combiner les détails par numéro de compte
                        const currentMap = {};
                        const currentMapN1 = {};
                        
                        currentDetails.forEach(d => { currentMap[d.compte] = d; });
                        if (showComparison) {
                            currentDetailsN1.forEach(d => { currentMapN1[d.compte] = d; });
                        }
                        
                        // Tous les comptes
                        const allComptes = new Set([
                            ...Object.keys(currentMap),
                            ...(showComparison ? Object.keys(currentMapN1) : [])
                        ]);
                        
                        return Array.from(allComptes).sort().map((compte, idx) => {
                            const currentDetail = currentMap[compte];
                            const currentDetailN1 = currentMapN1[compte];
                            
                            return (
                                <DetailRow 
                                    key={`${compte}-${idx}`}
                                    compte={compte}
                                    libelle={currentDetail?.libelle || currentDetailN1?.libelle || ''}
                                    monthMontant={currentDetail?.montant || 0}
                                    ytdMontant={currentDetail?.montant || 0}
                                    monthCa={currentCa}
                                    ytdCa={currentCa}
                                    monthMontantN1={currentDetailN1?.montant}
                                    ytdMontantN1={currentDetailN1?.montant}
                                    monthCaN1={currentCaN1}
                                    ytdCaN1={currentCaN1}
                                    showComparison={showComparison}
                                    displayMode={displayMode}
                                    isCharge={isCharge}
                                />
                            );
                        });
                    })()}
                </div>
            )}
        </>
    );
};

// Labels pour distinguer les restaurants similaires
const restaurantLabels = {
    "Jacques Balma": "Balma",
    "Jacques Blagnac": "Blagnac",
    "Jacques Fenouillet": "Fenouillet",
};

// Restaurant Card Component
const RestaurantCard = ({ restaurant, isSelected, onClick }) => {
    const logo = restaurantLogos[restaurant.name];
    const label = restaurantLabels[restaurant.name];
    
    return (
        <div
            data-testid={`restaurant-card-${restaurant.id}`}
            onClick={onClick}
            className={cn(
                "relative cursor-pointer rounded-xl overflow-hidden transition-all duration-300",
                "border-2 hover:shadow-lg",
                isSelected 
                    ? "border-amber-500 shadow-lg shadow-amber-500/20" 
                    : "border-transparent hover:border-border"
            )}
        >
            <div className="aspect-[4/3] bg-[#1a1a1a] flex flex-col items-center justify-center p-4">
                {logo ? (
                    <img 
                        src={logo} 
                        alt={restaurant.name}
                        className={cn(
                            "max-w-full object-contain",
                            label ? "max-h-[70%]" : "max-h-full"
                        )}
                    />
                ) : (
                    <Building2 className="w-12 h-12 text-muted-foreground" />
                )}
                {label && (
                    <span className="mt-2 text-white text-sm font-medium tracking-wide">
                        {label}
                    </span>
                )}
            </div>
            {isSelected && (
                <div className="absolute inset-0 border-2 border-amber-500 rounded-xl pointer-events-none" />
            )}
        </div>
    );
};

export default function ExploitationPage() {
    const { user } = useAuth();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const currentMonth = new Date().getMonth() + 1;
        return currentMonth > 1 ? currentMonth - 1 : 1;
    });
    const [selectedYear] = useState(2026);
    const [exploitationData, setExploitationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [compareMode, setCompareMode] = useState('n1'); // 'n1' ou 'budget'
    const [showYtd, setShowYtd] = useState(false); // false = Mois, true = YTD
    const [showLanding, setShowLanding] = useState(false); // Atterrissage année pleine
    const [landingData, setLandingData] = useState(null);
    const [loadingLanding, setLoadingLanding] = useState(false);
    const [isConsolidated, setIsConsolidated] = useState(false); // Mode consolidation
    const [consolidationRestaurants, setConsolidationRestaurants] = useState([]); // Restaurants inclus dans la consolidation
    const [showConsolidationDialog, setShowConsolidationDialog] = useState(false);
    const [pendingConsolidation, setPendingConsolidation] = useState([]);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportFormat, setExportFormat] = useState('excel');
    const [exportPeriod, setExportPeriod] = useState('month');
    const [exportMonth, setExportMonth] = useState(selectedMonth);
    const [selectedExportRestaurants, setSelectedExportRestaurants] = useState([]);
    const [exporting, setExporting] = useState(false);
    
    const availableMonths = MONTHS_FR;
    
    // Ouvrir le dialogue d'export
    const openExportDialog = () => {
        // Pré-sélectionner le restaurant actuel ou tous si consolidé
        if (isConsolidated) {
            setSelectedExportRestaurants(consolidationRestaurants.length ? consolidationRestaurants : restaurants.map(r => r.name));
        } else if (selectedRestaurant) {
            setSelectedExportRestaurants([selectedRestaurant.name]);
        } else {
            setSelectedExportRestaurants([]);
        }
        setExportMonth(selectedMonth);
        if (showLanding) setExportPeriod('landing');
        setShowExportDialog(true);
    };
    
    // Toggle sélection d'un restaurant pour l'export
    const toggleExportRestaurant = (name) => {
        setSelectedExportRestaurants(prev => {
            if (prev.includes(name)) {
                return prev.filter(n => n !== name);
            } else {
                return [...prev, name];
            }
        });
    };
    
    // Sélectionner/Désélectionner tous les restaurants
    const toggleAllRestaurants = () => {
        if (selectedExportRestaurants.length === restaurants.length) {
            setSelectedExportRestaurants([]);
        } else {
            setSelectedExportRestaurants(restaurants.map(r => r.name));
        }
    };
    
    // Fonction d'export
    const handleExport = async () => {
        if (selectedExportRestaurants.length === 0) {
            toast.error('Veuillez sélectionner au moins un restaurant');
            return;
        }
        
        setExporting(true);
        try {
            const token = localStorage.getItem('token');
            
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/exploitation/export-multi`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        restaurants: selectedExportRestaurants,
                        month: exportMonth,
                        year: selectedYear,
                        format: exportFormat,
                        period: exportPeriod
                    })
                }
            );
            
            if (!response.ok) {
                throw new Error('Erreur lors de l\'export');
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const monthLabel = MONTHS_FR.find(m => m.value === exportMonth)?.label;
            const periodLabel = exportPeriod === 'landing'
                ? `Atterrissage_${monthLabel}`
                : exportPeriod === 'ytd' ? `YTD_${monthLabel}` : monthLabel;
            a.download = `exploitation_${periodLabel}_${selectedYear}.${exportFormat === 'excel' ? 'xlsx' : 'pdf'}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast.success(`Export ${exportFormat.toUpperCase()} téléchargé (${selectedExportRestaurants.length} restaurant${selectedExportRestaurants.length > 1 ? 's' : ''})`);
            setShowExportDialog(false);
        } catch (error) {
            console.error('Erreur export:', error);
            toast.error('Erreur lors de l\'export');
        } finally {
            setExporting(false);
        }
    };
    
    // Charger les restaurants
    useEffect(() => {
        const loadRestaurants = async () => {
            try {
                const response = await restaurantsApi.getAll();
                const allRestaurants = response.data || [];
                
                // Filtrer les restaurants exclus (pas de données SharePoint)
                let filtered = allRestaurants.filter(r => !EXCLUDED_RESTAURANTS.includes(r.name));
                
                // Ajouter les restaurants spécifiques à la page Compte d'exploitation (admin uniquement)
                if (user?.role === 'admin') {
                    EXTRA_EXPLOITATION_RESTAURANTS.forEach(extra => {
                        if (!filtered.some(r => r.name === extra.name)) {
                            filtered.push(extra);
                        }
                    });
                }
                
                // Note: Le filtrage par utilisateur est déjà fait côté backend via restaurantsApi.getAll()
                // Pas besoin de filtrer à nouveau ici
                
                // Trier selon l'ordre personnalisé (Ma Biche en premier)
                filtered.sort((a, b) => {
                    const indexA = RESTAURANT_ORDER.indexOf(a.name);
                    const indexB = RESTAURANT_ORDER.indexOf(b.name);
                    // Si pas dans la liste, mettre à la fin
                    const orderA = indexA === -1 ? 999 : indexA;
                    const orderB = indexB === -1 ? 999 : indexB;
                    return orderA - orderB;
                });
                
                setRestaurants(filtered);
                // L'auto-sélection du premier restaurant est gérée par un autre useEffect
            } catch (error) {
                console.error('Erreur chargement restaurants:', error);
                toast.error('Erreur lors du chargement des restaurants');
            } finally {
                setLoading(false);
            }
        };
        
        loadRestaurants();
    }, [user]);
    
    // Auto-sélectionner le premier restaurant au chargement initial
    useEffect(() => {
        if (restaurants.length > 0 && !selectedRestaurant && !isConsolidated) {
            setSelectedRestaurant(restaurants[0]);
        }
    }, [restaurants, selectedRestaurant, isConsolidated]);
    
    // Charger les données d'exploitation
    const loadExploitationData = useCallback(async () => {
        if (!selectedRestaurant && !isConsolidated) return;
        
        setLoadingData(true);
        try {
            let response;
            if (isConsolidated) {
                // Mode consolidation - priorité sur selectedRestaurant
                response = await exploitationApi.getConsolidated(selectedMonth, selectedYear, consolidationRestaurants);
            } else {
                // Mode restaurant individuel
                response = await exploitationApi.getData(
                    selectedRestaurant.name,
                    selectedMonth,
                    selectedYear
                );
            }
            setExploitationData(response.data);
        } catch (error) {
            console.error('Erreur chargement exploitation:', error);
            if (error.response?.status === 404) {
                toast.error('Données comptables non disponibles');
            } else if (error.response?.status === 403) {
                toast.error('Accès non autorisé à la consolidation');
            } else {
                toast.error('Erreur lors du chargement des données');
            }
            setExploitationData(null);
        } finally {
            setLoadingData(false);
        }
    }, [selectedRestaurant, selectedMonth, selectedYear, isConsolidated, consolidationRestaurants]);
    
    useEffect(() => {
        loadExploitationData();
    }, [loadExploitationData]);
    
    // Atterrissage année pleine
    const loadLanding = useCallback(async () => {
        if (!showLanding || (!selectedRestaurant && !isConsolidated)) return;
        setLoadingLanding(true);
        try {
            const response = isConsolidated
                ? await exploitationApi.getLandingConsolidated(selectedMonth, selectedYear, consolidationRestaurants)
                : await exploitationApi.getLanding(selectedRestaurant.name, selectedMonth, selectedYear);
            setLandingData(response.data);
        } catch (error) {
            console.error('Erreur chargement atterrissage:', error);
            toast.error(error.response?.status === 404 ? "Atterrissage indisponible (pas de budget pour ce restaurant)" : "Erreur lors du calcul de l'atterrissage");
            setLandingData(null);
        } finally {
            setLoadingLanding(false);
        }
    }, [showLanding, selectedRestaurant, selectedMonth, selectedYear, isConsolidated, consolidationRestaurants]);
    
    useEffect(() => {
        loadLanding();
    }, [loadLanding]);
    
    const openConsolidationDialog = () => {
        setPendingConsolidation(consolidationRestaurants.length ? consolidationRestaurants : restaurants.map(r => r.name));
        setShowConsolidationDialog(true);
    };
    
    const applyConsolidation = () => {
        if (pendingConsolidation.length === 0) {
            toast.error('Sélectionnez au moins un restaurant');
            return;
        }
        setConsolidationRestaurants(pendingConsolidation);
        setIsConsolidated(true);
        setSelectedRestaurant(null);
        setShowConsolidationDialog(false);
    };
    
    const isAllConsolidated = isConsolidated && consolidationRestaurants.length === restaurants.length;
    
    const monthData = exploitationData?.month_data;
    const ytdData = exploitationData?.ytd_data;
    const monthDataN1 = exploitationData?.month_data_n1;
    const ytdDataN1 = exploitationData?.ytd_data_n1;
    const byRestaurant = exploitationData?.by_restaurant || null; // Données par restaurant pour tooltip consolidation
    const budgetMonth = exploitationData?.month_data_budget || null;
    const budgetYtd = exploitationData?.ytd_data_budget || null;
    const hasBudget = !!budgetMonth;
    const isBudgetMode = compareMode === 'budget' && hasBudget;
    
    // Rebasculer sur N-1 si le restaurant sélectionné n'a pas de budget
    useEffect(() => {
        if (compareMode === 'budget' && !loadingData && !hasBudget) {
            setCompareMode('n1');
        }
    }, [compareMode, hasBudget, loadingData]);
    const selectedMonthLabel = MONTHS_FR.find(m => m.value === selectedMonth)?.label || '';
    
    // Atterrissage : valeurs affichées et comparaison (budget annuel ou réel N-1 annuel)
    const isLandingMode = showLanding && !!landingData?.landing_data;
    const landingCompare = isLandingMode ? (compareMode === 'budget' ? landingData.budget_year : landingData.n1_year) : null;
    const landingByRestaurant = isLandingMode && landingData.by_restaurant
        ? Object.fromEntries(Object.entries(landingData.by_restaurant).map(([name, d]) => [name, {
            month_data: d.landing_data, month_data_budget: d.budget_year, month_data_n1: d.n1_year
        }]))
        : null;
    const landingParams = landingData?.params;
    
    return (
        <div className="flex h-screen bg-background" data-testid="exploitation-page">
            <Sidebar onCollapse={setSidebarCollapsed} />
            
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header title="Soldes Intermédiaires de Gestion" />
                
                <main className="flex-1 overflow-auto p-6">
                    {/* Sélecteur de restaurant */}
                    <div className="mb-6">
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                            {loading ? (
                                [...Array(8)].map((_, i) => (
                                    <Skeleton key={i} className="aspect-[4/3] rounded-xl" />
                                ))
                            ) : (
                                <>
                                    {restaurants.map(restaurant => (
                                        <RestaurantCard
                                            key={restaurant.id}
                                            restaurant={restaurant}
                                            isSelected={!isConsolidated && selectedRestaurant?.id === restaurant.id}
                                            onClick={() => {
                                                setIsConsolidated(false);
                                                setSelectedRestaurant(restaurant);
                                            }}
                                        />
                                    ))}
                                    {/* Bouton Consolidation - uniquement pour les admins */}
                                    {user?.role === 'admin' && (
                                        <button
                                            onClick={openConsolidationDialog}
                                            className={cn(
                                                "relative h-20 md:h-24 rounded-xl overflow-hidden cursor-pointer transition-all duration-200",
                                                "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                                                "hover:scale-105 hover:shadow-lg",
                                                "bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-dashed",
                                                isConsolidated 
                                                    ? "ring-2 ring-primary ring-offset-2 shadow-lg border-primary" 
                                                    : "border-border hover:border-primary/50"
                                            )}
                                            data-testid="consolidated-exploitation-btn"
                                        >
                                            <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                                                <Layers className="w-6 h-6 mb-1 text-primary" />
                                                <span className="font-semibold text-xs text-center">
                                                    {isConsolidated ? `Consolidé (${consolidationRestaurants.length}/${restaurants.length})` : 'Consolider…'}
                                                </span>
                                            </div>
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    
                    {/* En-tête avec restaurant et sélecteur de mois */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <FileSpreadsheet className="w-6 h-6 text-amber-600" />
                            <h2 className="text-xl font-semibold">
                                {isConsolidated
                                    ? (isAllConsolidated ? 'Consolidation (Tous restaurants)' : `Consolidation (${consolidationRestaurants.length} restaurants)`)
                                    : (selectedRestaurant?.name || 'Sélectionnez un restaurant')}
                            </h2>
                            {isConsolidated && !isAllConsolidated && (
                                <button
                                    onClick={openConsolidationDialog}
                                    className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 hover:bg-amber-100 max-w-[520px] truncate"
                                    title={consolidationRestaurants.join(', ')}
                                    data-testid="consolidation-scope-chip"
                                >
                                    {consolidationRestaurants.map(n => SHORT_NAMES[n] || n).join(' · ')}
                                </button>
                            )}
                        </div>
                        
                        <Select
                            value={selectedMonth.toString()}
                            onValueChange={(value) => setSelectedMonth(parseInt(value))}
                        >
                            <SelectTrigger className="w-[180px]" data-testid="month-selector">
                                <SelectValue placeholder="Sélectionner un mois" />
                            </SelectTrigger>
                            <SelectContent>
                                {availableMonths.map(month => (
                                    <SelectItem key={month.value} value={month.value.toString()}>
                                        {month.label} {selectedYear}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        
                        {/* Toggle comparaison */}
                        <div className="flex items-center gap-4 ml-4">
                            {/* Atterrissage année pleine */}
                            <button
                                onClick={() => setShowLanding(v => !v)}
                                className={cn(
                                    "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5",
                                    showLanding ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                )}
                                title="Projection année pleine : réalisé à date + budget restant ajusté à la tendance du CA"
                                data-testid="landing-toggle-btn"
                            >
                                <PlaneLanding className="w-4 h-4" />
                                Atterrissage
                            </button>
                            
                            <div className="h-6 w-px bg-slate-200" />
                            
                            {/* Toggle YTD */}
                            <label className={cn("flex items-center gap-2 cursor-pointer", showLanding && "opacity-40 pointer-events-none")}>
                                <input
                                    type="checkbox"
                                    checked={showYtd}
                                    onChange={(e) => setShowYtd(e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                                />
                                <span className="text-sm font-medium text-slate-700">YTD</span>
                            </label>
                            
                            <div className="h-6 w-px bg-slate-200" />
                            
                            <button
                                onClick={() => setCompareMode('n1')}
                                className={cn(
                                    "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                                    compareMode === 'n1' 
                                        ? "bg-amber-500 text-white" 
                                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                )}
                            >
                                vs N-1
                            </button>
                            <button
                                onClick={() => (hasBudget || isLandingMode) && setCompareMode('budget')}
                                disabled={!hasBudget && !isLandingMode}
                                className={cn(
                                    "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                                    compareMode === 'budget' && (hasBudget || isLandingMode)
                                        ? "bg-amber-500 text-white" 
                                        : hasBudget
                                            ? "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                            : "bg-slate-100 text-slate-400 cursor-not-allowed"
                                )}
                                title={hasBudget ? undefined : "Budget non disponible (consolidé ou fichier budget SharePoint introuvable)"}
                                data-testid="compare-budget-btn"
                            >
                                vs Budget
                            </button>
                            
                            <div className="h-6 w-px bg-slate-200" />
                            
                            {/* Bouton Export */}
                            <Button
                                onClick={openExportDialog}
                                variant="outline"
                                className="flex items-center gap-2"
                                data-testid="export-btn"
                            >
                                <Download className="w-4 h-4" />
                                Exporter
                            </Button>
                        </div>
                    </div>
                    
                    {showLanding && landingParams && (
                        <div className="text-xs text-violet-800 bg-violet-50 border border-violet-200 rounded-md px-3 py-2 mb-3 flex flex-wrap gap-x-4 gap-y-1" data-testid="landing-params">
                            <span className="font-semibold">Atterrissage {selectedYear} :</span>
                            <span>réalisé janv.–{selectedMonthLabel.toLowerCase()} + projection {landingParams.months_realised < 12 ? `${MONTHS_FR[landingParams.months_realised]?.label.toLowerCase()}–déc.` : '—'}</span>
                            {landingParams.trend != null && <span>· Tendance CA : <b>{(landingParams.trend * 100).toFixed(1)} %</b> du budget ({formatMoney(landingParams.ca_real_ytd)} vs {formatMoney(landingParams.ca_budget_ytd)})</span>}
                            {landingParams.ratio_achats != null && <span>· Achats consommés : <b>{(landingParams.ratio_achats * 100).toFixed(1)} %</b> du CA (ratio réalisé YTD)</span>}
                            <span>· Masse salariale, frais généraux : budget restant</span>
                            {landingData?.landing_missing?.length > 0 && <span className="text-amber-700">· Hors : {landingData.landing_missing.join(', ')} (pas de budget)</span>}
                        </div>
                    )}
                    
                    {isBudgetMode && isConsolidated && exploitationData?.budget_missing?.length > 0 && !showLanding && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="budget-missing-note">
                            Budget consolidé hors : {exploitationData.budget_missing.join(', ')} (pas de fichier budget SharePoint)
                        </p>
                    )}
                    
                    {/* Tableau SIG */}
                    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                        {/* En-tête du tableau */}
                        {(compareMode === 'n1' || isBudgetMode || isLandingMode) ? (
                            <div className="grid grid-cols-[1fr,110px,65px,110px,65px,85px] gap-0 bg-slate-800 text-white font-semibold text-sm">
                                <div className="py-3 px-4">SOLDES INTERMÉDIAIRES DE GESTION</div>
                                <div className={cn(
                                    "py-3 px-2 text-right",
                                    isLandingMode ? "bg-violet-900/40" : showYtd ? "bg-emerald-900/30" : "bg-blue-900/30"
                                )}>
                                    {isLandingMode ? `Atterrissage ${selectedYear}` : showYtd ? `YTD ${selectedYear}` : `${selectedMonthLabel} ${selectedYear}`}
                                </div>
                                <div className={cn(
                                    "py-3 px-2 text-right text-xs",
                                    showYtd ? "bg-emerald-900/20 text-emerald-200" : "bg-blue-900/20 text-blue-200"
                                )}>% CA</div>
                                <div className="py-3 px-2 text-right bg-slate-700/50">
                                    {isLandingMode
                                        ? (compareMode === 'budget' ? `Budget ${selectedYear}` : `Réel ${selectedYear - 1}`)
                                        : isBudgetMode
                                            ? (showYtd ? 'Budget YTD' : `Budget ${selectedMonthLabel}`)
                                            : (showYtd ? `YTD ${selectedYear - 1}` : `${selectedMonthLabel} ${selectedYear - 1}`)}
                                </div>
                                <div className="py-3 px-2 text-right text-xs bg-slate-700/30 text-slate-300">% CA</div>
                                <div className="py-3 px-2 text-right bg-amber-900/30 text-amber-200">Écart</div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-[1fr,100px,70px,100px,70px] gap-0 bg-slate-800 text-white font-semibold text-sm">
                                <div className="py-3 px-4">SOLDES INTERMÉDIAIRES DE GESTION</div>
                                <div className="py-3 px-2 text-right bg-blue-900/30">{selectedMonthLabel}</div>
                                <div className="py-3 px-2 text-right bg-blue-900/20 text-blue-200">% CA</div>
                                <div className="py-3 px-2 text-right bg-emerald-900/30">YTD</div>
                                <div className="py-3 px-2 text-right bg-emerald-900/20 text-emerald-200">% CA</div>
                            </div>
                        )}
                        
                        {(loadingData || (showLanding && loadingLanding)) ? (
                            <div className="p-8">
                                {[...Array(12)].map((_, i) => (
                                    <Skeleton key={i} className="h-10 mb-2" />
                                ))}
                            </div>
                        ) : isLandingMode ? (
                            <div>
                                {sigStructure.map((item) => (
                                    <SigRow
                                        key={`landing-${item.id}`}
                                        item={item}
                                        monthValue={landingData.landing_data?.[item.id]}
                                        ytdValue={landingData.landing_data?.[item.id]}
                                        monthCa={landingData.landing_data?.ca_net}
                                        ytdCa={landingData.landing_data?.ca_net}
                                        monthDetails={landingData.landing_data?.details?.[item.id] || []}
                                        ytdDetails={landingData.landing_data?.details?.[item.id] || []}
                                        monthValueN1={landingCompare?.[item.id] ?? null}
                                        ytdValueN1={landingCompare?.[item.id] ?? null}
                                        monthCaN1={landingCompare?.ca_net}
                                        ytdCaN1={landingCompare?.ca_net}
                                        monthDetailsN1={landingCompare?.details?.[item.id] || []}
                                        ytdDetailsN1={landingCompare?.details?.[item.id] || []}
                                        showComparison={!!landingCompare}
                                        strictEcart={compareMode === 'budget'}
                                        displayMode="month"
                                        byRestaurant={landingByRestaurant}
                                        isConsolidated={isConsolidated}
                                        compareMode={compareMode === 'budget' ? 'budget' : 'n1'}
                                        periodLabel={`Atterrissage ${selectedYear}`}
                                    />
                                ))}
                            </div>
                        ) : exploitationData?.error ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>{exploitationData.error}</p>
                            </div>
                        ) : (
                            <div>
                                {sigStructure.map((item) => (
                                    <SigRow
                                        key={item.id}
                                        item={item}
                                        monthValue={monthData?.[item.id]}
                                        ytdValue={ytdData?.[item.id]}
                                        monthCa={monthData?.ca_net}
                                        ytdCa={ytdData?.ca_net}
                                        monthDetails={monthData?.details?.[item.id] || []}
                                        ytdDetails={ytdData?.details?.[item.id] || []}
                                        // Données N-1 (ou Budget en mode vs Budget)
                                        monthValueN1={isBudgetMode ? (budgetMonth?.[item.id] ?? null) : monthDataN1?.[item.id]}
                                        ytdValueN1={isBudgetMode ? (budgetYtd?.[item.id] ?? null) : ytdDataN1?.[item.id]}
                                        monthCaN1={isBudgetMode ? budgetMonth?.ca_net : monthDataN1?.ca_net}
                                        ytdCaN1={isBudgetMode ? budgetYtd?.ca_net : ytdDataN1?.ca_net}
                                        monthDetailsN1={isBudgetMode ? (budgetMonth?.details?.[item.id] || []) : (monthDataN1?.details?.[item.id] || [])}
                                        ytdDetailsN1={isBudgetMode ? (budgetYtd?.details?.[item.id] || []) : (ytdDataN1?.details?.[item.id] || [])}
                                        showComparison={compareMode === 'n1' || isBudgetMode}
                                        strictEcart={isBudgetMode}
                                        displayMode={showYtd ? 'ytd' : 'month'}
                                        // Données par restaurant pour tooltip consolidation
                                        byRestaurant={byRestaurant}
                                        isConsolidated={isConsolidated}
                                        compareMode={isBudgetMode ? 'budget' : 'n1'}
                                        periodLabel={showYtd ? `YTD ${selectedYear}` : `${selectedMonthLabel} ${selectedYear}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </main>
            </div>
            
            {/* Dialog de choix des restaurants à consolider */}
            <Dialog open={showConsolidationDialog} onOpenChange={setShowConsolidationDialog}>
                <DialogContent className="sm:max-w-md" data-testid="consolidation-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-amber-600" />
                            Consolider des restaurants
                        </DialogTitle>
                        <DialogDescription>
                            Choisissez les restaurants à additionner dans le compte d'exploitation consolidé.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-2">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium">Restaurants</label>
                            <button
                                onClick={() => setPendingConsolidation(pendingConsolidation.length === restaurants.length ? [] : restaurants.map(r => r.name))}
                                className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                                data-testid="consolidation-toggle-all"
                            >
                                {pendingConsolidation.length === restaurants.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 border rounded-lg bg-slate-50">
                            {restaurants.map(resto => (
                                <label
                                    key={resto.name}
                                    className={cn(
                                        "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                                        pendingConsolidation.includes(resto.name) ? "bg-amber-100 text-amber-800" : "hover:bg-slate-100"
                                    )}
                                >
                                    <input
                                        type="checkbox"
                                        checked={pendingConsolidation.includes(resto.name)}
                                        onChange={() => setPendingConsolidation(prev => prev.includes(resto.name) ? prev.filter(n => n !== resto.name) : [...prev, resto.name])}
                                        className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                                        data-testid={`consolidation-check-${resto.name}`}
                                    />
                                    <span className="text-sm truncate">{resto.name}</span>
                                </label>
                            ))}
                        </div>
                        <p className="text-xs text-slate-500">
                            {pendingConsolidation.length} restaurant{pendingConsolidation.length > 1 ? 's' : ''} sélectionné{pendingConsolidation.length > 1 ? 's' : ''}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConsolidationDialog(false)}>Annuler</Button>
                        <Button onClick={applyConsolidation} className="bg-amber-500 hover:bg-amber-600" data-testid="consolidation-apply-btn">
                            Consolider ({pendingConsolidation.length})
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Dialog d'export */}
            <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Download className="w-5 h-5 text-amber-600" />
                            Exporter le compte d'exploitation
                        </DialogTitle>
                        <DialogDescription>
                            Sélectionnez les restaurants et la période à exporter
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Sélection des restaurants */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">Restaurants</label>
                                <button 
                                    onClick={toggleAllRestaurants}
                                    className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                                >
                                    {selectedExportRestaurants.length === restaurants.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg bg-slate-50">
                                {restaurants.map(resto => (
                                    <label 
                                        key={resto.name}
                                        className={cn(
                                            "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                                            selectedExportRestaurants.includes(resto.name) 
                                                ? "bg-amber-100 text-amber-800" 
                                                : "hover:bg-slate-100"
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedExportRestaurants.includes(resto.name)}
                                            onChange={() => toggleExportRestaurant(resto.name)}
                                            className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                                        />
                                        <span className="text-sm truncate">{resto.name}</span>
                                    </label>
                                ))}
                            </div>
                            <p className="text-xs text-slate-500">
                                {selectedExportRestaurants.length} restaurant{selectedExportRestaurants.length > 1 ? 's' : ''} sélectionné{selectedExportRestaurants.length > 1 ? 's' : ''}
                                {selectedExportRestaurants.length > 1 && ' (+ feuille consolidée)'}
                            </p>
                        </div>
                        
                        {/* Choix du format */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Format d'export</label>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setExportFormat('excel')}
                                    className={cn(
                                        "flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all",
                                        exportFormat === 'excel'
                                            ? "border-amber-500 bg-amber-50 text-amber-700"
                                            : "border-slate-200 hover:border-slate-300"
                                    )}
                                >
                                    <Table2 className="w-5 h-5" />
                                    <span className="font-medium">Excel</span>
                                </button>
                                <button
                                    onClick={() => setExportFormat('pdf')}
                                    className={cn(
                                        "flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all",
                                        exportFormat === 'pdf'
                                            ? "border-amber-500 bg-amber-50 text-amber-700"
                                            : "border-slate-200 hover:border-slate-300"
                                    )}
                                >
                                    <FileText className="w-5 h-5" />
                                    <span className="font-medium">PDF</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Choix de la période */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Type de période</label>
                            <div className="flex gap-3">
                                {[
                                    { value: 'month', label: 'Mois' },
                                    { value: 'ytd', label: 'YTD' },
                                    { value: 'landing', label: 'Atterrissage' },
                                ].map(p => (
                                    <button
                                        key={p.value}
                                        data-testid={`export-period-${p.value}`}
                                        onClick={() => setExportPeriod(p.value)}
                                        className={cn(
                                            "flex-1 px-4 py-2 rounded-lg border-2 transition-all text-center",
                                            exportPeriod === p.value
                                                ? "border-amber-500 bg-amber-50 text-amber-700"
                                                : "border-slate-200 hover:border-slate-300"
                                        )}
                                    >
                                        <span className="font-medium text-sm">{p.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Sélection du mois */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">
                                {exportPeriod === 'ytd' ? 'YTD jusqu\'à' : exportPeriod === 'landing' ? 'Réalisé jusqu\'à' : 'Mois'}
                            </label>
                            <Select value={String(exportMonth)} onValueChange={(v) => setExportMonth(Number(v))}>
                                <SelectTrigger className="w-full" data-testid="export-month-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableMonths.map(m => (
                                        <SelectItem key={m.value} value={String(m.value)}>
                                            {m.label} {selectedYear}
                                            {exportPeriod === 'ytd' && ` (Janv. → ${m.label})`}
                                            {exportPeriod === 'landing' && ` (réalisé → ${m.label}, projeté → Déc.)`}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {exportPeriod === 'ytd' && (
                                <p className="text-xs text-slate-500">
                                    Cumul de Janvier à {MONTHS_FR.find(m => m.value === exportMonth)?.label} {selectedYear}
                                </p>
                            )}
                            {exportPeriod === 'landing' && (
                                <p className="text-xs text-slate-500" data-testid="export-landing-note">
                                    Année pleine {selectedYear} : réalisé Janvier → {MONTHS_FR.find(m => m.value === exportMonth)?.label} + projection jusqu'à Décembre, comparé au Budget {selectedYear} et au Réel {selectedYear - 1}
                                </p>
                            )}
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowExportDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleExport} 
                            disabled={exporting || selectedExportRestaurants.length === 0}
                            className="bg-amber-500 hover:bg-amber-600"
                        >
                            {exporting ? (
                                <>
                                    <span className="animate-spin mr-2">⏳</span>
                                    Export en cours...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4 mr-2" />
                                    Télécharger
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
